import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { Haptics } from "@capacitor/haptics";
import { Howl } from "howler";
import {
  NotificationSoundDedup,
  NOTIFICATION_SOUND_COOLDOWN_MS,
} from "../helpers/notification-sound-dedup";

/**
 * Sound Service
 *
 * Joue les sons de notification + vibration côté JS (web et native).
 *
 * Implémentation Phase 2 : Howler au lieu de HTML5 `Audio`.
 *
 * Pourquoi Howler :
 * - **Autoplay policy** : `Audio.play()` est bloqué dans la WebView Capacitor
 *   tant qu'aucune interaction utilisateur n'a eu lieu. Howler gère bien mieux
 *   ce cas (queue de lecture, retry interne, support Web Audio API en fallback).
 * - **Préload fiable** : Howler attend que le fichier soit chargé avant de
 *   tenter `play()` (évite les NotAllowedError sur première lecture).
 * - **Volume / mute / état lisible** par sprite : utile pour stop fluide.
 *
 * NOTE : sur Capacitor native, le natif PusherBackgroundService /
 * PusherBackgroundManager / MyFirebaseMessagingService est l'autorité unique
 * sur le son (cf pusher.service.ts:sendVibrate qui skip le JS si nativeReady).
 * Ce service ne joue donc le son que pour le web (PWA) ou en fallback quand
 * le natif n'est pas ready (par ex. Android 12+ background restrict avant
 * subscription_succeeded).
 */
@Injectable({
  providedIn: "root",
})
export class SoundService {
  private howlCache: Map<string, Howl> = new Map();
  private isNative: boolean = false;
  private lastPlayAt: Map<string, number> = new Map();
  /**
   * Debounce par type. `notify` aligné sur le cooldown natif (3s).
   * Les autres types (success/error, toggle prestataire) gardent un debounce court.
   */
  private readonly DEBOUNCE_MS_BY_TYPE: Record<string, number> = {
    notify: NOTIFICATION_SOUND_COOLDOWN_MS,
    success: 800,
    error: 800,
  };

  /** Instance Howl dédiée à l'alerte urgente (loopée). */
  private urgentHowl: Howl | null = null;
  /** Timer vibration répétée pendant le loop urgent. */
  private urgentVibrationTimer: ReturnType<typeof setInterval> | null = null;
  /** Timer sécurité : arrêt automatique après TTL max (90s). */
  private urgentAutoStopTimer: ReturnType<typeof setTimeout> | null = null;
  /** TTL max du loop urgent en ms (aligné sur le TTL demande prestataire). */
  private readonly URGENT_AUTO_STOP_MS = 90_000;
  /** Intervalle de vibration pendant le loop (ms). */
  private readonly URGENT_VIBRATION_INTERVAL_MS = 3_000;

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
  }

  /**
   * Get the correct audio path for the current platform.
   * Capacitor copie /assets dans /www/assets au build → même path pour web et
   * native.
   */
  private getAudioPath(type: string): string {
    return `assets/audio/${type}.mp3`;
  }

  /**
   * Sources audio pour un type donné.
   * `notify` utilise le WAV (marimba) en priorité, MP3 en fallback.
   * Les autres types restent sur MP3 uniquement.
   */
  private getAudioSources(type: string): string[] {
    if (type === 'notify') {
      return ['assets/audio/notify.wav', 'assets/audio/notify.mp3'];
    }
    return [this.getAudioPath(type)];
  }

  /**
   * Get or create the Howl instance for a given sound type.
   * Préload silencieux au premier appel.
   */
  private getHowl(type: string): Howl {
    const cached = this.howlCache.get(type);
    if (cached) {
      return cached;
    }

    const sources = this.getAudioSources(type);
    const howl = new Howl({
      src: sources,
      // html5: true force le HTML5 Audio backend plutôt que Web Audio API.
      // Web Audio API peut bloquer en background tab (suspension du
      // AudioContext) — HTML5 Audio est plus fiable pour les notifications.
      html5: true,
      preload: true,
      volume: 1.0,
      onloaderror: (_id: number, error: any) => {
        console.debug(`[SoundService] Failed to load audio: ${sources[0]}`, error);
      },
      onplayerror: (_id: number, error: any) => {
        console.debug(`[SoundService] Howler play error (autoplay blocked?):`, error);
        // Howler gère le retry interne via .once('unlock', ...) si
        // l'autoplay est débloqué par une interaction.
      },
    });

    this.howlCache.set(type, howl);
    return howl;
  }

  /**
   * Play sound and trigger vibration.
   * @param duration Vibration duration in milliseconds (default: 2500ms)
   * @param type Sound type: "notify" (default), "success", or "error"
   */
  /**
   * Son de notification temps réel (Pusher). Passe par la dédup centralisée si
   * `eventData` est fourni ; sinon délègue à {@link sendVibrate}.
   */
  async playNotificationSound(
    eventData?: Record<string, unknown> | null
  ): Promise<void> {
    if (NotificationSoundDedup.shouldSkipSound(eventData)) {
      return;
    }
    NotificationSoundDedup.markSoundPlayed(eventData);
    await this.sendVibrate(2500, "notify", { skipDedup: true });
  }

  async sendVibrate(
    duration = 2500,
    type = "notify",
    options?: { skipDedup?: boolean }
  ) {
    try {
      const debounceMs =
        this.DEBOUNCE_MS_BY_TYPE[type] ?? this.DEBOUNCE_MS_BY_TYPE["notify"];
      const now = Date.now();
      const last = this.lastPlayAt.get(type) ?? 0;
      if (now - last < debounceMs) {
        return;
      }
      if (type === "notify" && !options?.skipDedup) {
        if (NotificationSoundDedup.isGlobalCooldownActive()) {
          return;
        }
        NotificationSoundDedup.markSoundPlayed();
      }
      this.lastPlayAt.set(type, now);
      const howl = this.getHowl(type);
      // Stop la précédente lecture pour éviter le chevauchement (équivalent du
      // `currentTime = 0` HTML5).
      howl.stop();
      howl.play();

      if (this.isNative) {
        try {
          await Haptics.vibrate({ duration: duration });
        } catch (vibrateError) {
          console.debug("[SoundService] Vibration failed:", vibrateError);
        }
      }
    } catch (error) {
      console.debug("[SoundService] Error playing sound or vibration:", error);
    }
  }

  /** Play notification sound (alias for sendVibrate with notify type). */
  async playNotification() {
    await this.sendVibrate(2500, "notify");
  }

  /** Play success sound. */
  async playSuccess() {
    await this.sendVibrate(2500, "success");
  }

  /** Play error sound. */
  async playError() {
    await this.sendVibrate(2500, "error");
  }

  /** Clear audio cache (useful for memory management). */
  clearCache() {
    this.howlCache.forEach((howl) => {
      howl.unload();
    });
    this.howlCache.clear();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Alerte urgente (nouvelle intervention prestataire)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Joue la sonnerie urgente loopée pour les nouvelles missions prestataire.
   * Utilisé uniquement sur la couche JS (web/PWA ou natif non-ready).
   * Sur Capacitor native avec nativeReady, c'est le natif qui gère le loop.
   */
  async playUrgentAlert(): Promise<void> {
    this.stopUrgentAlert();
    try {
      const howl = this.getUrgentHowl();
      howl.stop();
      howl.play();

      if (this.isNative) {
        await this.triggerStrongVibration();
        this.urgentVibrationTimer = setInterval(async () => {
          await this.triggerStrongVibration();
        }, this.URGENT_VIBRATION_INTERVAL_MS);
      }

      this.urgentAutoStopTimer = setTimeout(() => {
        this.stopUrgentAlert();
      }, this.URGENT_AUTO_STOP_MS);
    } catch (error) {
      console.debug("[SoundService] Error playing urgent alert:", error);
    }
  }

  /** Arrête la sonnerie urgente (acceptation / expiration / annulation demande). */
  stopUrgentAlert(): void {
    if (this.urgentHowl?.playing()) {
      this.urgentHowl.stop();
    }
    if (this.urgentVibrationTimer !== null) {
      clearInterval(this.urgentVibrationTimer);
      this.urgentVibrationTimer = null;
    }
    if (this.urgentAutoStopTimer !== null) {
      clearTimeout(this.urgentAutoStopTimer);
      this.urgentAutoStopTimer = null;
    }
  }

  private getUrgentHowl(): Howl {
    if (this.urgentHowl) {
      return this.urgentHowl;
    }
    this.urgentHowl = new Howl({
      src: ["assets/audio/urgent_alert.mp3"],
      html5: true,
      preload: true,
      volume: 1.0,
      loop: true,
      onloaderror: (_id: number, error: any) => {
        console.debug("[SoundService] Failed to load urgent_alert audio", error);
      },
      onplayerror: (_id: number, error: any) => {
        console.debug("[SoundService] Urgent Howler play error (autoplay blocked?):", error);
      },
    });
    return this.urgentHowl;
  }

  private async triggerStrongVibration(): Promise<void> {
    if (!this.isNative) return;
    try {
      await Haptics.vibrate({ duration: 500 });
      await new Promise<void>((resolve) => setTimeout(resolve, 200));
      await Haptics.vibrate({ duration: 500 });
    } catch (e) {
      console.debug("[SoundService] Strong vibration failed:", e);
    }
  }
}
